import { left, right } from '../../../../shared/core/Either';
import { APIResponse } from '../../../../shared/infra/api/APIResponse';
import { BaseAPI } from '../../../../shared/infra/api/BaseAPI';
import { Result } from '../../../../shared/core/Result';
import { IAuthService } from '../../../users/services/authService';
import {
  APNPushRequestDTO,
  CommunityRequestDTO,
  GCMPushRequestDTO,
} from '../dtos/CommunityRequest.dto';
import { CommunityDetailsResponse, CommunityResponse } from '../infra/types';
import { CommunityModel } from '../models/Community.model';
import { CommunityUtil } from '../utils/CommunityUtil';
import { BasicCommunityModel } from '../models/BasicCommunity.model';
import { BasicCommunitiesResponseDTO } from '../dtos/BasicCommunity.dto';
import { BasicCommunityUtil } from '../utils/BasicCommunityUtil';
import { AxiosError } from 'axios';
import { PaginationDataModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { PaginationUtil } from '../../../../shared/components/pagination/utils/PaginationUtils';
import { UpdateCommunityAppVersionRequest } from '../redux/operators/updateCommunityAppVersion';

export interface ICommunityService {
  getCommunities(
    offset?: number,
    filter?: string,
    dir?: number,
    search?: string,
  ): Promise<APIResponse<PaginationDataModel<CommunityModel[]>>>;
  createCommunity(
    communityRequest: CommunityRequestDTO,
  ): Promise<APIResponse<void>>;
  updateCommunity(
    communityRequest: CommunityRequestDTO,
  ): Promise<APIResponse<void>>;
  getBasicCommunities(): Promise<APIResponse<BasicCommunityModel[]>>;
  getCommunity(): Promise<APIResponse<CommunityModel>>;
  replaceAPNPush(apnPushRequest: APNPushRequestDTO): Promise<APIResponse<void>>;
  replaceGCMPush(gcmPushRequest: GCMPushRequestDTO): Promise<APIResponse<void>>;
  buildCommunityFeed(
    communityRequest: CommunityRequestDTO,
  ): Promise<APIResponse<void>>;
  sendCommunitySummary(
    communityRequest: CommunityRequestDTO,
  ): Promise<APIResponse<void>>;
  updateCommunityV2Feeds(communityId: string): Promise<APIResponse<void>>;
  updateCommunityV2Likes(communityId: string): Promise<APIResponse<void>>;
  buildKeys(): Promise<APIResponse<void>>;
  updateCommunityAppVersion(
    request: UpdateCommunityAppVersionRequest,
  ): Promise<APIResponse<void>>;
}

export class CommunityService extends BaseAPI implements ICommunityService {
  protected onFailureExecuteImpl(error: AxiosError<any>): Promise<any> {
    throw new Error(`Method not implemented. ${error.message}`);
  }
  constructor(authService: IAuthService) {
    super(authService);
  }

  hasToken(): boolean {
    return this.authService.isUserAuthenticated();
  }

  public async updateCommunityAppVersion(
    request: UpdateCommunityAppVersionRequest,
  ): Promise<APIResponse<void>> {
    try {
      await this.post<void>({
        url: '/v2/community/app-update',
        requiresToken: true,
        payload: request,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async buildKeys(): Promise<APIResponse<void>> {
    try {
      const response = await this.patch<void>({
        url: '/v2/scripts/community/objectId',
        requiresToken: true,
      });

      console.log(response);

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async getCommunity(): Promise<APIResponse<CommunityModel>> {
    try {
      const response = await this.get<CommunityDetailsResponse>({
        url: '/v1/community',
        requiresToken: true,
      });

      const community = response.data.data;

      return right(
        Result.ok<CommunityModel>(CommunityUtil.mapToViewModel(community)),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async getCommunities(
    offset?: number,
    filter?: string,
    dir?: number,
    search?: string,
  ): Promise<APIResponse<PaginationDataModel<CommunityModel[]>>> {
    try {
      const response = await this.get<CommunityResponse>({
        url: '/v1/community/admin',
        requiresToken: true,
        config: { params: { offset, filter, dir, search } },
      });
      const { data, meta } = response.data;

      return right(
        Result.ok<PaginationDataModel<CommunityModel[]>>({
          model: data.map((item) => CommunityUtil.mapToViewModel(item)),
          meta: PaginationUtil.mapToViewModel(meta),
        }),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async getBasicCommunities(): Promise<
    APIResponse<BasicCommunityModel[]>
  > {
    try {
      const response = await this.get<BasicCommunitiesResponseDTO>({
        url: '/v1/administrator/communities/basic',
        requiresToken: true,
      });

      const communities = response.data.communities;

      return right(
        Result.ok<BasicCommunityModel[]>(
          communities.map((item) => BasicCommunityUtil.mapToViewModel(item)),
        ),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async createCommunity(
    communityRequest: CommunityRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.post<CommunityRequestDTO>({
        url: '/v1/community',
        requiresToken: true,
        payload: communityRequest,
      });
      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async updateCommunityV2Feeds(
    communityId: string,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch<void>({
        url: `/v2/scripts/community/feed/${communityId}`,
        requiresToken: true,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }
  public async updateCommunityV2Likes(
    communityId: string,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch<void>({
        url: `/v2/scripts/community/likes/${communityId}`,
        requiresToken: true,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async updateCommunity(
    communityRequest: CommunityRequestDTO,
  ): Promise<APIResponse<void>> {
    if (!communityRequest.id)
      return left({ message: 'Community was invalid or could not be found' });

    try {
      await this.patch<CommunityRequestDTO>({
        url: `/v1/community/${communityRequest.id}`,
        requiresToken: true,
        payload: communityRequest,
      });
      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async replaceAPNPush(
    apnPushRequest: APNPushRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch<APNPushRequestDTO>({
        url: `/v1/community/${apnPushRequest.id || ''}/push/apn`,
        requiresToken: true,
        payload: apnPushRequest,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async replaceGCMPush(
    gcmPushRequest: GCMPushRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch<GCMPushRequestDTO>({
        url: `/v1/community/${gcmPushRequest.id || ''}/push/gcm`,
        requiresToken: true,
        payload: gcmPushRequest,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async buildCommunityFeed(
    communityRequest: CommunityRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.post({
        url: `/v2/scripts/feeds/${
          communityRequest.id ? communityRequest.id : ''
        }`,
        requiresToken: true,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async sendCommunitySummary(
    communityRequest: CommunityRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.post({
        url: `/v1/community/contact/${
          communityRequest.id ? communityRequest.id : ''
        }`,
        requiresToken: true,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }
}
